import React from "react";
import { formatarParaReais } from "../../services/utilitarios";
import { tokens } from "../modal-form/tokens";
import { CobrancaConciliavel, CategoriaConciliacao } from "../conciliacao-cobrancas-asaas";
import { useNomeCaucao } from "../../hooks/useNomeCaucao";

/** Para onde vai cada cobrança que o cliente já tem no Asaas. */
export type DestinoCobranca = "caucao" | "aluguel" | "lancamento";

export interface EscolhaCobranca {
    destino: DestinoCobranca;
    idCategoria?: string;
}

export type ClassificacaoCobrancas = Record<string, EscolhaCobranca>;

const semAcento = (texto?: string) =>
    (texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();

// Pela marca que o servidor manda (a caução pode ter sido renomeada, ex.: "ADESÃO"); o nome só
// decide quando a resposta é de uma versão do servidor que ainda não mandava a marca.
export const ehCategoriaDeCaucao = (categoria?: CategoriaConciliacao) =>
    categoria?.ehCaucao !== undefined ? categoria.ehCaucao : semAcento(categoria?.nome).includes("CAUCAO");

const formatarData = (data?: string) => {
    if (!data) return "";
    const [ano, mes, dia] = String(data).substring(0, 10).split("-");
    return dia && mes && ano ? `${dia}/${mes}/${ano}` : String(data);
};

/**
 * Sugestão inicial: caução e categoria vêm do palpite do servidor (nome da categoria na descrição da
 * cobrança); cobrança com cara de aluguel vai para aluguel. O resto nasce sem destino, para o operador
 * decidir. Sugerir é o que torna a migração de um cliente com dezenas de cobranças viável.
 */
export function sugerirClassificacao(
    cobrancas: CobrancaConciliavel[], categorias: CategoriaConciliacao[],
): ClassificacaoCobrancas {
    const porId = new Map(categorias.map((c) => [c.id, c]));
    const sugestao: ClassificacaoCobrancas = {};

    (cobrancas || []).forEach((cobranca) => {
        // Vencimento de OUTRA assinatura só vem marcado quando a descrição nomeia uma categoria: marcá-lo
        // como aluguel por padrão traria parcelas antigas para o contrato sem o locador ter escolhido.
        if (cobranca.parecidaComAluguel && !cobranca.idAssinatura) {
            sugestao[cobranca.id] = { destino: "aluguel" };
            return;
        }
        if (cobranca.idCategoriaSugerida) {
            const categoria = porId.get(cobranca.idCategoriaSugerida);
            sugestao[cobranca.id] = ehCategoriaDeCaucao(categoria)
                ? { destino: "caucao" }
                : { destino: "lancamento", idCategoria: cobranca.idCategoriaSugerida };
        }
    });

    return sugestao;
}

const VALOR_NAO_TRAZER = "";
const VALOR_CAUCAO = "caucao";
const VALOR_ALUGUEL = "aluguel";

const caixa: React.CSSProperties = {
    padding: tokens.spacingMd,
    borderRadius: tokens.radiusMd,
    fontSize: tokens.fontSm,
    background: tokens.bgLight,
    border: `1px solid ${tokens.borderLight}`,
    color: tokens.textSecondary,
};

/**
 * A consulta ao Asaas falhou: pode haver caução paga que a tela não está mostrando. Aviso explícito,
 * porque seguir em silêncio é o que faz o cliente receber boleto de algo que já pagou.
 */
export function AvisoCobrancasIndisponiveis() {
    const nomeCaucao = useNomeCaucao();
    return (
        <div style={{ ...caixa, background: tokens.warningBg, border: `1px solid ${tokens.warningColor}` }}>
            <strong>Não foi possível listar as cobranças deste cliente no Asaas.</strong>
            <div style={{ marginTop: tokens.spacingXs }}>
                Se ele já tem {nomeCaucao.minusculo} ou acerto pago por lá, cadastre o contrato e use "Conciliar cobranças do
                Asaas" no menu da locação antes de cobrar, para não cobrar duas vezes.
            </div>
        </div>
    );
}

export function TextoDeAbertura({ quantidade }: { quantidade: number }) {
    return (
        <div style={{ fontSize: tokens.fontBase, color: tokens.textPrimary }}>
            Este cliente já tem {quantidade} cobrança{quantidade > 1 ? "s" : ""} no Asaas que nenhum contrato do
            sistema usa. Diga o que é cada uma: nada é criado agora, e o que você apontar preenche o cadastro.
        </div>
    );
}

const valorDaEscolha = (escolha?: EscolhaCobranca) => {
    if (!escolha) return VALOR_NAO_TRAZER;
    if (escolha.destino === "caucao") return VALOR_CAUCAO;
    if (escolha.destino === "aluguel") return VALOR_ALUGUEL;
    return escolha.idCategoria ? `cat:${escolha.idCategoria}` : VALOR_NAO_TRAZER;
};

const escolhaDoValor = (valor: string): EscolhaCobranca | null => {
    if (!valor) return null;
    if (valor === VALOR_CAUCAO) return { destino: "caucao" };
    if (valor === VALOR_ALUGUEL) return { destino: "aluguel" };
    return { destino: "lancamento", idCategoria: valor.replace("cat:", "") };
};

/** Nome da origem de um grupo de vencimentos, do jeito que o locador reconhece. */
export const tituloDaOrigem = (cobranca: CobrancaConciliavel) =>
    cobranca.assinaturaAtiva
        ? `Outra assinatura ativa${cobranca.assinaturaDescricao ? `: ${cobranca.assinaturaDescricao}` : ""}`
        : "Assinatura excluída ou encerrada no Asaas";

function OpcoesDeDestino({ categorias }: { categorias: CategoriaConciliacao[] }) {
    const nomeCaucao = useNomeCaucao();
    return (
        <>
            <option value={VALOR_NAO_TRAZER}>Não trazer</option>
            <option value={VALOR_ALUGUEL}>Aluguel</option>
            <option value={VALOR_CAUCAO}>{nomeCaucao.titulo}</option>
            {categorias.filter((c) => !ehCategoriaDeCaucao(c)).map((categoria) => (
                <option key={categoria.id} value={`cat:${categoria.id}`}>
                    Lançamento: {categoria.nome}
                </option>
            ))}
        </>
    );
}

/**
 * Cabeçalho de um grupo de vencimentos que vieram da mesma assinatura. Diz a origem e, quando são
 * vários, deixa classificar todos de uma vez: a manutenção cobrada todo mês vira um lançamento
 * parcelado com uma escolha só, em vez de doze.
 */
function CabecalhoDoGrupo({
    cobrancas, classificacao, categorias, onClassificarTodas,
}: {
    cobrancas: CobrancaConciliavel[];
    classificacao: ClassificacaoCobrancas;
    categorias: CategoriaConciliacao[];
    onClassificarTodas: (escolha: EscolhaCobranca | null) => void;
}) {
    const primeira = cobrancas[0];
    const valores = new Set(cobrancas.map((c) => valorDaEscolha(classificacao[c.id])));
    const valorComum = valores.size === 1 ? Array.from(valores)[0] : "__misto";
    const total = cobrancas.reduce((soma, c) => soma + (Number(c.valor) || 0), 0);

    return (
        <div
            style={{
                display: "flex", gap: tokens.spacingSm, alignItems: "center", flexWrap: "wrap",
                margin: `${tokens.spacingSm} 0 ${tokens.spacingXs}`, padding: tokens.spacingSm,
                borderRadius: tokens.radiusMd, background: tokens.bgLight, border: `1px dashed ${tokens.borderDefault}`,
            }}
        >
            <div style={{ minWidth: 190, flex: 1 }}>
                <div style={{ fontWeight: 600, color: tokens.textPrimary, fontSize: tokens.fontSm }}>{tituloDaOrigem(primeira)}</div>
                <div style={{ fontSize: tokens.fontXs, color: tokens.textMuted }}>
                    {cobrancas.length === 1
                        ? `1 vencimento de ${formatarParaReais(total)}`
                        : `${cobrancas.length} vencimentos, somando ${formatarParaReais(total)}. Na mesma categoria, viram um lançamento parcelado.`}
                </div>
            </div>
            {cobrancas.length > 1 && (
                <select
                    className="form-control"
                    style={{ fontSize: tokens.fontSm, maxWidth: 260 }}
                    value={valorComum}
                    onChange={(e) => {
                        if (e.target.value !== "__misto") onClassificarTodas(escolhaDoValor(e.target.value));
                    }}
                    title="Classificar todos os vencimentos desta assinatura"
                >
                    {valorComum === "__misto" && <option value="__misto">Escolhas diferentes</option>}
                    <OpcoesDeDestino categorias={categorias} />
                </select>
            )}
        </div>
    );
}

/**
 * Uma cobrança e a decisão sobre ela. Fica em campo próprio para o modal poder paginar: com a lista
 * inteira numa página só, o operador rolava até o meio, clicava em confirmar e deixava cobrança sem
 * decidir sem perceber.
 */
export function LinhaCobranca({
    cobranca, categorias, escolha, onClassificar, posicao, total, grupo,
}: {
    cobranca: CobrancaConciliavel;
    categorias: CategoriaConciliacao[];
    escolha?: EscolhaCobranca;
    onClassificar: (idCobranca: string, escolha: EscolhaCobranca | null) => void;
    posicao: number;
    total: number;
    /** Presente na primeira linha de cada assinatura de origem: abre o grupo com o cabeçalho dela. */
    grupo?: {
        cobrancas: CobrancaConciliavel[];
        classificacao: ClassificacaoCobrancas;
        onClassificarTodas: (escolha: EscolhaCobranca | null) => void;
    };
}) {
    return (
        <>
        {grupo && grupo.cobrancas.length > 0 && (
            <CabecalhoDoGrupo
                cobrancas={grupo.cobrancas}
                classificacao={grupo.classificacao}
                categorias={categorias}
                onClassificarTodas={grupo.onClassificarTodas}
            />
        )}
        <div
            style={{
                display: "flex", gap: tokens.spacingSm, alignItems: "center", flexWrap: "wrap",
                padding: tokens.spacingSm, borderRadius: tokens.radiusMd,
                background: escolha ? tokens.primaryLight : tokens.bgWhite,
                border: `1px solid ${escolha ? tokens.primaryColor : tokens.borderDefault}`,
            }}
        >
            <div style={{ minWidth: 190, flex: 1 }}>
                <div style={{ fontSize: tokens.fontXs, color: tokens.textMuted }}>
                    Cobrança {posicao} de {total}
                </div>
                <span style={{ fontWeight: 600, color: tokens.textPrimary }}>{formatarParaReais(cobranca.valor)}</span>
                <span style={{ fontSize: tokens.fontXs, color: cobranca.estaPaga ? "#1b6b3a" : tokens.textMuted, marginLeft: tokens.spacingXs }}>
                    {cobranca.estaPaga
                        ? `paga${cobranca.dataPagamento ? ` em ${formatarData(cobranca.dataPagamento)}` : ""}`
                        : `vence ${formatarData(cobranca.vencimento)}`}
                </span>
                {cobranca.descricao && (
                    <div style={{ fontSize: tokens.fontXs, color: tokens.textSecondary }}>{cobranca.descricao}</div>
                )}
            </div>

            <select
                className="form-control"
                style={{ fontSize: tokens.fontSm, maxWidth: 260 }}
                value={valorDaEscolha(escolha)}
                onChange={(e) => onClassificar(cobranca.id, escolhaDoValor(e.target.value))}
            >
                <OpcoesDeDestino categorias={categorias} />
            </select>
        </div>
        </>
    );
}

/**
 * Consolidado do que a classificação vai produzir. Fica na última página do modal: é a revisão antes
 * de confirmar, e é onde o operador percebe que esqueceu de apontar alguma cobrança.
 */
export function ResumoClassificacao({
    cobrancas, classificacao, categorias,
}: {
    cobrancas: CobrancaConciliavel[];
    classificacao: ClassificacaoCobrancas;
    categorias?: CategoriaConciliacao[];
}) {
    const nomeCaucao = useNomeCaucao();
    const lista = cobrancas || [];
    const por = (destino: DestinoCobranca) => lista.filter((c) => classificacao[c.id]?.destino === destino);

    const caucoes = por("caucao");
    const alugueis = por("aluguel");
    const lancamentos = por("lancamento");
    const semDestino = lista.filter((c) => !classificacao[c.id]);
    const totalCaucao = caucoes.reduce((soma, c) => soma + (Number(c.valor) || 0), 0);
    const caucoesPagas = caucoes.filter((c) => c.estaPaga).length;

    // Mesma regra do servidor: vencimentos da MESMA assinatura na MESMA categoria viram um lançamento
    // parcelado; avulsas e grupos de um só ficam soltos.
    const nomeDaCategoria = new Map((categorias || []).map((c) => [c.id, c.nome]));
    const porGrupo = new Map<string, CobrancaConciliavel[]>();
    lancamentos.forEach((c) => {
        const idCategoria = classificacao[c.id]?.idCategoria;
        if (!c.idAssinatura || !idCategoria) return;
        const chave = `${c.idAssinatura}|${idCategoria}`;
        porGrupo.set(chave, [...(porGrupo.get(chave) || []), c]);
    });
    const parcelamentos = Array.from(porGrupo.entries())
        .filter(([, lista]) => lista.length > 1)
        .map(([chave, lista]) => ({
            categoria: nomeDaCategoria.get(chave.split("|")[1]) || "lançamento",
            parcelas: lista.length,
            total: lista.reduce((soma, c) => soma + (Number(c.valor) || 0), 0),
        }));
    const lancamentosEmParcelamento = parcelamentos.reduce((soma, p) => soma + p.parcelas, 0);
    const lancamentosSoltos = lancamentos.length - lancamentosEmParcelamento;

    return (
        <div>
            <div style={caixa}>
                <strong style={{ color: tokens.textPrimary }}>Com isso, o cadastro fica assim:</strong>
                <ul style={{ margin: `${tokens.spacingXs} 0 0 1rem`, padding: 0 }}>
                    {caucoes.length > 0 && (
                        <li>
                            {nomeCaucao.titulo} de <strong>{formatarParaReais(totalCaucao)}</strong>
                            {caucoes.length > 1 ? ` em ${caucoes.length} parcelas` : ""}
                            {caucoesPagas > 0 ? `, ${caucoesPagas} já paga${caucoesPagas > 1 ? "s" : ""}` : ""}.
                            O campo de {nomeCaucao.minusculo} passa a ser definido por estas cobranças.
                        </li>
                    )}
                    {parcelamentos.map((p, indice) => (
                        <li key={`${indice}-${p.categoria}`}>
                            Lançamento parcelado de <strong>{p.categoria}</strong> em {p.parcelas} parcelas, somando{" "}
                            {formatarParaReais(p.total)}, cada parcela ligada à sua cobrança.
                        </li>
                    ))}
                    {lancamentosSoltos > 0 && (
                        <li>
                            {lancamentosSoltos} lançamento{lancamentosSoltos > 1 ? "s" : ""} na locação, já
                            ligado{lancamentosSoltos > 1 ? "s" : ""} à cobrança de origem.
                        </li>
                    )}
                    {alugueis.length > 0 && (
                        <li>
                            {alugueis.length} cobrança{alugueis.length > 1 ? "s" : ""} de aluguel {alugueis.length > 1 ? "serão casadas" : "será casada"} com
                            a parcela do mesmo período depois de o contrato ser criado. A que não cair em nenhum
                            ciclo entra como parcela na data dela, com o valor da cobrança.
                        </li>
                    )}
                    {caucoes.length === 0 && lancamentos.length === 0 && alugueis.length === 0 && (
                        <li>Nada será trazido do Asaas para este contrato.</li>
                    )}
                </ul>
                <div style={{ fontSize: tokens.fontXs, color: tokens.textMuted, marginTop: tokens.spacingXs }}>
                    Nenhuma cobrança nova é emitida para o que você apontou aqui.
                </div>
            </div>

            {semDestino.length > 0 && (
                <div style={{ ...caixa, marginTop: tokens.spacingSm, background: tokens.warningBg, border: `1px solid ${tokens.warningColor}` }}>
                    <strong style={{ color: tokens.textPrimary }}>
                        {semDestino.length} cobrança{semDestino.length > 1 ? "s" : ""} sem destino
                    </strong>
                    <div style={{ marginTop: tokens.spacingXs }}>
                        {semDestino.map((c) => (
                            <div key={c.id}>
                                {formatarParaReais(c.valor)} · {c.estaPaga ? "paga" : `vence ${formatarData(c.vencimento)}`}
                                {c.descricao ? ` · ${c.descricao}` : ""}
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: tokens.spacingXs }}>
                        Ficam como estão no Asaas, sem entrar neste contrato. Volte se alguma delas for a {nomeCaucao.minusculo}{" "}
                        ou um acerto deste cliente.
                    </div>
                </div>
            )}
        </div>
    );
}
